import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Instagram } from "lucide-react";

const COMPANY = [
  { label: "Discover", to: "/" },
  { label: "Creative Agent", to: "/create" },
  { label: "Submit a Reel", to: "/submit" },
];

const UTILITY = [
  { label: "Terms of Service", to: "/" },
  { label: "Privacy Policy", to: "/" },
];

export default function Footer() {
  const textRef = useRef(null);
  const [viewBox, setViewBox] = useState("0 0 1200 120");

  useEffect(() => {
    const measure = () => {
      const t = textRef.current;
      if (!t) return;
      let b;
      try { b = t.getBBox(); } catch { return; }
      if (!b || !b.width) return;
      // crop the bottom so the wordmark sits lower with a strong "cut off" look
      setViewBox(`${b.x} ${b.y} ${b.width} ${b.height * 0.7}`);
    };
    measure();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure);
    const t1 = setTimeout(measure, 200);
    const t2 = setTimeout(measure, 800);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  return (
    <footer data-testid="site-footer" className="relative bg-[#0a0a0a] text-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14">
          {/* Left — iOS app coming soon */}
          <div>
            <p className="text-sm font-mono uppercase tracking-[0.2em] text-white/40 mb-3">iOS app · coming soon</p>
            <h2 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.05] mb-8">
              Cook smarter,<br />on the go.
            </h2>
            <button
              data-testid="download-app-btn"
              className="group inline-flex items-center gap-3 bg-[#f5f5f5] text-[#0a0a0a] border-2 border-[#f5f5f5] rounded-2xl px-5 py-2.5 hover:opacity-90 transition-opacity"
              title="iOS app coming soon"
            >
              <svg viewBox="0 0 384 512" className="w-7 h-7 fill-current shrink-0" aria-hidden="true">
                <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
              </svg>
              <span className="text-left leading-none">
                <span className="block text-[11px] font-normal tracking-tight">Download on the</span>
                <span className="block text-[22px] font-semibold leading-tight tracking-tight">App Store</span>
              </span>
            </button>
          </div>

          {/* Right — link columns */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-8">
            <div>
              <p className="text-sm font-mono uppercase tracking-[0.15em] text-white/40 mb-4">Social</p>
              <a href="https://www.instagram.com/flavourintelligence" target="_blank" rel="noreferrer"
                data-testid="footer-instagram"
                className="inline-flex items-center gap-1.5 text-white/85 hover:text-white transition-colors">
                <Instagram className="w-4 h-4" /> @flavourintelligence
              </a>
            </div>
            <div>
              <p className="text-sm font-mono uppercase tracking-[0.15em] text-white/40 mb-4">Company</p>
              <ul className="space-y-2.5">
                {COMPANY.map((l) => (
                  <li key={l.label}>
                    <Link to={l.to} className="text-white/85 hover:text-white transition-colors">{l.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-sm font-mono uppercase tracking-[0.15em] text-white/40 mb-4">Utility</p>
              <ul className="space-y-2.5">
                {UTILITY.map((l) => (
                  <li key={l.label}>
                    <Link to={l.to} className="text-white/85 hover:text-white transition-colors">{l.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Company / legal line */}
        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div className="text-sm text-white/50 leading-relaxed">
            <p>
              © 2026 Flavour Intelligence. A product of{" "}
              <a href="https://fullerbyte.com" target="_blank" rel="noreferrer"
                data-testid="footer-fullerbyte"
                className="inline-flex items-center gap-0.5 text-white font-semibold hover:opacity-80 transition-opacity">
                Fullerbyte Pte Ltd <ArrowUpRight className="w-3.5 h-3.5" />
              </a>
            </p>
            <p className="mt-1 text-white/40">68 Circular Road, #02-01, 049422, Singapore</p>
          </div>
        </div>
      </div>

      {/* Giant wordmark — drawn as SVG so it always scales to the full screen width */}
      <div data-testid="footer-wordmark" className="select-none w-full leading-[0]" aria-hidden="true">
        <svg viewBox={viewBox} width="100%" preserveAspectRatio="xMidYMin meet" className="block w-full h-auto">
          <text
            ref={textRef}
            x="0"
            y="100"
            fontFamily="'Inter Tight', ui-sans-serif, system-ui, sans-serif"
            fontWeight="800"
            fontSize="100"
            letterSpacing="-4"
            fill="#ffffff"
          >
            FlavourIntelligence<tspan fontSize="30" dy="-46">™</tspan>
          </text>
        </svg>
      </div>
    </footer>
  );
}
